"use client";

import type { WhisperJson } from "@teksta/shared-types";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { whisperJsonToCues } from "subtitletools";
import type { EditorRecentSession, EditorSource, EditorSourceSessionSave } from "./adapters";
import { defaultAudioMixForMedia } from "./audioMix";
import { ensurePersistentStorage, loadSession, saveSession } from "./persistence";
import { loadSubtitleFiles } from "./subtitleFileLoader";
import { createTekstaRunInitialDoc } from "./TekstaRunInitialDoc";
import {
  createTekstaRunMediaReference,
  type TekstaRunMediaReference,
  tekstaRunSessionKey,
} from "./TekstaRunSourceMedia";

export type { TekstaRunMediaReference } from "./TekstaRunSourceMedia";

export function isTekstaRunMedia(media: unknown): media is TekstaRunMediaReference {
  const candidate = media as Partial<TekstaRunMediaReference> | null;
  return (
    candidate !== null &&
    typeof candidate === "object" &&
    candidate.kind === "teksta-run" &&
    typeof candidate.runId === "string" &&
    typeof candidate.jobId === "string" &&
    typeof candidate.url === "string" &&
    typeof candidate.name === "string" &&
    typeof candidate.mimeType === "string" &&
    (candidate.vocalStemUrl === null || typeof candidate.vocalStemUrl === "string") &&
    (candidate.instrumentalStemUrl === null || typeof candidate.instrumentalStemUrl === "string")
  );
}

export function useTekstaRunSource({
  runId,
  jobId,
  whisperJson,
  fileName,
  isAudio,
  hasVocalStem,
  hasInstrumentalStem,
}: {
  runId: string;
  jobId: string;
  whisperJson: WhisperJson;
  fileName: string;
  isAudio: boolean;
  hasVocalStem: boolean;
  hasInstrumentalStem: boolean;
}): EditorSource {
  const t = useTranslations("mediaTools.editor.errors");
  const sourceCues = useMemo(() => whisperJsonToCues(whisperJson), [whisperJson]);
  const sourceMedia = useMemo(
    () =>
      createTekstaRunMediaReference({
        runId,
        jobId,
        fileName,
        isAudio,
        hasVocalStem,
        hasInstrumentalStem,
      }),
    [fileName, hasInstrumentalStem, hasVocalStem, isAudio, jobId, runId]
  );
  const [media, setMedia] = useState<TekstaRunMediaReference | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);
  const pendingRestore = useRef<EditorRecentSession | null>(null);
  const pendingInitialCues = useRef(sourceCues);

  useEffect(() => {
    let cancelled = false;
    setMedia(null);
    setPreviewUrl(null);
    setRestored(false);
    pendingRestore.current = null;
    pendingInitialCues.current = sourceCues;

    void loadSession(tekstaRunSessionKey(runId)).then((saved) => {
      if (cancelled) return;
      pendingRestore.current = saved ? { ...saved, restored: true } : null;
      setMedia(sourceMedia);
      setPreviewUrl(sourceMedia.url);
    });

    return () => {
      cancelled = true;
    };
  }, [runId, sourceCues, sourceMedia]);

  const consumeLoadedDuration = useCallback(
    (duration: number) => {
      const saved = pendingRestore.current;
      pendingRestore.current = null;
      if (saved) {
        setRestored(true);
        return saved;
      }

      const cues = pendingInitialCues.current;
      pendingInitialCues.current = [];
      setRestored(false);
      return {
        fp: tekstaRunSessionKey(runId),
        meta: {
          name: sourceMedia.name,
          size: 0,
          type: sourceMedia.mimeType,
          lastModified: 0,
          duration,
        },
        doc: createTekstaRunInitialDoc({
          duration,
          whisperJson,
          isAudio,
          audioMix: defaultAudioMixForMedia(sourceMedia),
        }),
        originalCues: cues,
        savedAt: Date.now(),
        restored: false,
      } satisfies EditorRecentSession;
    },
    [runId, sourceMedia]
  );

  const persistSession = useCallback((input: EditorSourceSessionSave) => {
    if (!isTekstaRunMedia(input.media)) return;
    void saveSession({
      fp: tekstaRunSessionKey(input.media.runId),
      meta: {
        name: input.media.name,
        size: 0,
        type: input.media.mimeType,
        lastModified: 0,
        duration: input.duration,
      },
      doc: input.doc,
      originalCues: input.originalCues ?? undefined,
      savedAt: input.savedAt,
    });
  }, []);

  const persistStorage = useCallback(() => {
    void ensurePersistentStorage();
  }, []);
  const loadLocalizedSubtitleFiles = useCallback(
    (files: File[]) =>
      loadSubtitleFiles(files, {
        readError: t("subtitleRead"),
        emptyError: t("subtitleEmpty"),
      }),
    [t]
  );

  return {
    media,
    previewUrl,
    recent: [],
    restored,
    setRestored,
    audioAvailable: true,
    undecodableAudio: null,
    open: async () => {},
    removeRecent: () => {},
    consumeLoadedDuration,
    loadSubtitleFiles: loadLocalizedSubtitleFiles,
    saveSession: persistSession,
    ensurePersistentStorage: persistStorage,
  };
}
